// src/pages/MileageHistory.js
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TabContext from "@/contexts/TabContext";
import { dateFormatToISOString, useListPageFilter } from "@/utils";
import { Confirm, PageTabs, Pagination, Toast } from "@/components";

import { useGetMyInfoQuery } from "@/redux/memberApi";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import {
    useGetMileageChargeRequestAdminQuery,
    useGetMileageHistoryAdminQuery,
    usePostMileageChargeConfirmMutation,
} from "../../redux/adminMileageApi";
import { comma, dateFormatDateTime } from "../../utils";
import Select from "react-select";
import { memberStatusOptions, mileageChargeTypeOptions, mileageUsedTypeOptions } from "../../constants/selectOptions";

// 페이지 기본 필터
const pageFilter = {
    parameters: {
        page: 0,
        size: 20,
        query: "",
        login_id: "",
        rent_corp_name: "",
        started_at: "",
        ended_at: "",
    },
};
export const AdminMileageHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state: tabs, dispatch } = useContext(TabContext);

    // 탭(토글) 상태: 1 / 2
    const [currentTab, setCurrentTab] = useState(1);

    const { formData, handleChange, setFormData, parameters, toastMsg } = useListPageFilter(pageFilter, {
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        orderDetailPopupStatus: false,
        mileage: 0,
        name: "",
        mileageWithdraw: 0,
        triggerSearch: false,
    });

    const { confirmStatus, orderDetailPopupStatus, mileageWithdrawalPopupStatus, triggerSearch } = formData;

    // 탭 추가 로직
    useEffect(() => {
        const handleTabAdd = () => {
            const existingTab = tabs.find((tab) => tab.url.split("?")[0] === location.pathname);
            if (!existingTab) {
                dispatch({
                    type: "ADD_TAB",
                    payload: { url: location.pathname + location.search, title: "마일리지" },
                });
            }
        };
        handleTabAdd();
    }, [dispatch, location.pathname, location.search, tabs]);

    const { data: myInfoResponse, isMyInfoError, error: myInfoError } = useGetMyInfoQuery();

    const [postMileageConfirm] = usePostMileageChargeConfirmMutation();

    // currentTab 값에 따라 API 호출
    const {
        data: requestData,
        error: errorMileage,
        refetch: refetchReqeust,
    } = useGetMileageChargeRequestAdminQuery(parameters, {
        skip: currentTab !== 1 || !triggerSearch, // currentTab이 1가 아니거나 triggerSearch가 false면 스킵
    });

    const {
        data: historyData,
        error: errorPoint,
        refetch: refetchHistory,
    } = useGetMileageHistoryAdminQuery(parameters, {
        skip: currentTab !== 2 || !triggerSearch, // currentTab이 2가 아니거나 triggerSearch가 false면 스킵
    });

    // // 아래처럼 필요한 데이터를 선택해서 사용
    const data = currentTab === 1 ? requestData : historyData;
    const error = currentTab === 1 ? errorMileage : errorPoint;

    const handleCharge = async (id) => {
        let res = await postMileageConfirm(id);
        if (res.error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: res.error.message || "실패하였습니다. 다시 시도하여 주세요.",
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                toastMsg: res.message || "완료되었습니다.",
                confirmStatus: {
                    ...prev.confirmStatus,
                    confirmPopupStatus: false,
                },
            }));
            setFormData((prev) => ({
                ...prev,
                parameters: { ...prev.parameters, page: 0 },
                triggerSearch: true,
            }));
        }
    };

    const handleChargeRequest = (item) => {
        if (item.finishYn === "N") {
            let title = "마일리지 충전";
            let description = `${item.member?.primaryRentCorp?.corpName}님이 요청하신 ${comma(
                item.mileage
            )} 충전요청을 적립하시겠습니까? (입금확인 필수)`;
            if (item.chargeType === "MINUS") {
                title = "마일리지 환전";
                description = `${item.member?.primaryRentCorp?.corpName}님이 요청하신 ${comma(
                    item.mileage
                )} 환전요청을 완료처리하시겠습니까?`;
            }
            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: title,
                    confirmDescription: description,
                    confirmButtons: [
                        { label: "예", className: "btn-primary", onClick: () => handleCharge(item.id) },
                        {
                            label: "아니오",
                            className: "btn-cancel",
                            onClick: () =>
                                setFormData((prev2) => ({
                                    ...prev2,
                                    confirmStatus: { confirmPopupStatus: false },
                                })),
                        },
                    ],
                },
            }));
        }
    };

    // 컴포넌트 마운트 시 초기 데이터 조회
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            triggerSearch: true,
        }));
    }, []);

    // 검색 버튼 클릭
    const handleSearch = () => {
        setFormData((prev) => ({
            ...prev,
            parameters: { ...prev.parameters, page: 0 },
            triggerSearch: true,
        }));
    };

    const memberData = myInfoResponse?.data;

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management container">
                <PageTabs />

                <div className="table-container">
                    <div className="table-head">
                        <span className="head-tit">마일리지 이용 목록</span>
                        <div className="breadcrumbs">
                            <ul>
                                <li className="home"></li>
                                <li>ERP 관리</li>
                                <li>마일리지 이용 목록</li>
                            </ul>
                        </div>
                    </div>
                    <TableFilter formData={formData} handleChange={handleChange} setFormData={setFormData} handleSearch={handleSearch} />

                    <div className="table-content">
                        <div className="table-option">
                            <div className="table-caption">
                                <div className="toggle-list">
                                    <button
                                        type="button"
                                        className={`btn-toggle ${currentTab === 1 ? "active" : ""}`}
                                        onClick={() => setCurrentTab(1)}
                                    >
                                        마일리지 충/환전 요청
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-toggle ${currentTab === 2 ? "active" : ""}`}
                                        onClick={() => setCurrentTab(2)}
                                    >
                                        마일리지 이용내역
                                    </button>
                                </div>
                                <span className="data-count">
                                    전체 <b>{data?.pagination?.totalElements}</b>건
                                </span>
                            </div>
                            <div className="table-buttons"></div>
                        </div>

                        <div className="table-wrapper">
                            <div className="table">
                                {currentTab === 1 && (
                                    <table>
                                        <colgroup>
                                            <col width="8%" />
                                            <col width="15%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">회원 ID</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">업체명</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">요청마일리지</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">충전/적립</span>
                                                    </div>
                                                </th>

                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">입금자</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">출금 은행정보</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">날짜/시간</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">처리여부</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">처리시각</span>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data && data.data && data.data.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.member?.loginId || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.name || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{comma(item.mileage || "0")}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <button
                                                                    type="button"
                                                                    className={`btn-cell ${
                                                                        item.chargeType === "PLUS" ? "btn-outline" : "btn-cancel"
                                                                    }`}
                                                                    onClick={() => {}}
                                                                >
                                                                    {item.chargeType === "PLUS"
                                                                        ? "충전요청"
                                                                        : item.chargeType === "MINUS"
                                                                        ? "출금요청"
                                                                        : ""}
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.name || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {item.bankName || ""}
                                                                    <br />
                                                                    {item.bankAccount || ""}
                                                                    <br />
                                                                    {item.bankOwner || ""}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {dateFormatDateTime(item.createdAt, "YYYY-MM-DD HH:mm:ss")}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <button
                                                                    type="button"
                                                                    //className="btn-cell btn-outline"
                                                                    className={`btn-cell ${
                                                                        item.finishYn === "Y" ? "btn-outline" : "btn-cancel"
                                                                    }`}
                                                                    onClick={() => handleChargeRequest(item)}
                                                                >
                                                                    {item.finishYn === "Y" ? "완료" : item.finishYn === "N" ? "미완료" : ""}
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {dateFormatDateTime(item.finishedAt, "YYYY-MM-DD HH:mm:ss")}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}

                                {currentTab === 2 && (
                                    <table>
                                        <colgroup>
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">회원 ID</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">회원명</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">적립/사용</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">설명</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">사용내역</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">변경전</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">변경후</span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <span className="cell-tit">사용시각</span>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data && data.data && data.data.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.member?.loginId || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.member?.name || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <button
                                                                    type="button"
                                                                    className={`btn-cell ${
                                                                        item.usedType === "PLUS" ? "btn-outline" : "btn-cancel"
                                                                    }`}
                                                                    onClick={() => {}}
                                                                >
                                                                    {item.usedType === "PLUS"
                                                                        ? "적립"
                                                                        : item.usedType === "MINUS"
                                                                        ? "사용"
                                                                        : ""}
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{item.reason || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{comma(item.actionAmount || "0")}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{comma(item.beforeAmount || "0")}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">{comma(item.afterAmount || "0")}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell" data-align="center">
                                                                <span className="cell-desc">
                                                                    {dateFormatDateTime(item.createdAt, "YYYY-MM-DD HH:mm:ss")}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {data?.data?.length ? <Pagination pagination={data?.pagination} setFormData={setFormData} /> : null}
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus?.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}
        </>
    );
};

const TableFilter = ({ formData, setFormData, handleSearch }) => {
    const [filterOption, setFilterOption] = useState([]);
    const [clientLabel, setClientLabel] = useState({});
    const [statusLabel, setStatusLabel] = useState({});

    const { parameters } = formData;
    const [activeButton, setActiveButton] = useState(null);

    // const { data, error, isLoading } = useGetClientNameListQuery();

    // useEffect(() => {
    //     if (data?.data) {
    //         var res = [{ value: null, label: "전체" }];
    //         data?.data?.forEach((i) => {
    //             res.push({ value: i.id, label: i.clientCorName });
    //         });
    //         setFilterOption(res);
    //     }
    // }, [data]);

    const handleParameterChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                [name]: value,
            },
            triggerSearch: false,
        }));
    };

    const handleDateChange = (dates, type) => {
        const [start, end] = dates;

        if (type === 1) {
            setFormData((prev) => ({
                ...prev,
                parameters: {
                    ...prev.parameters,
                    started_at: start ? dateFormatToISOString(start, "start") : "",
                    ended_at: end ? dateFormatToISOString(end, "end") : "",
                },
                triggerSearch: false,
            }));
        }

        if (type === "service") {
            setFormData((prev) => ({
                ...prev,
                parameters: {
                    ...prev.parameters,
                    started_at: start ? dateFormatToISOString(start, "start") : "",
                    ended_at: end ? dateFormatToISOString(end, "end") : "",
                },
                triggerSearch: false,
            }));
        }

        setActiveButton(null);
    };

    const setDateRange = (days, buttonIndex) => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() - days);

        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                startedAt: dateFormatToISOString(start, "start"),
                endedAt: dateFormatToISOString(now, "end"),
            },
        }));

        setActiveButton(buttonIndex);
    };

    return (
        <div className="table-filter">
            <div className="filter-row">
                {/*
                    <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">상태 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-select">
                            <Select
                                options={ReservationStatusType}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="진행현황"
                                value={statusLabel}
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: {
                                            ...prev.parameters,
                                            status: option.value,
                                        },
                                    }));
                                    setStatusLabel(option);
                                }}
                            />
                        </div>
                    </div>
                </div>
                     */}

                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">등록일 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-input">
                            <DatePicker
                                locale={ko}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={20}
                                dateFormat="yyyy.MM.dd"
                                onKeyDown={(e) => e.preventDefault()}
                                selectsRange={true}
                                startDate={parameters.started_at ? new Date(parameters.started_at) : null}
                                endDate={parameters.ended_at ? new Date(parameters.ended_at) : null}
                                onChange={(e) => handleDateChange(e, 1)}
                            />
                        </div>
                    </div>
                </div>
                {/*
                    <div className="filter-item">
                    <div className="item-content">
                        <div className="item-buttons">
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 0 ? "active" : ""}`}
                                onClick={() => setDateRange(0, 0)}
                            >
                                당일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 15 ? "active" : ""}`}
                                onClick={() => setDateRange(15, 15)}
                            >
                                15일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 30 ? "active" : ""}`}
                                onClick={() => setDateRange(30, 30)}
                            >
                                1개월
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 90 ? "active" : ""}`}
                                onClick={() => setDateRange(90, 90)}
                            >
                                3개월
                            </button>
                        </div>
                    </div>
                </div>
                     */}
            </div>

            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-select">
                            <Select
                                options={mileageChargeTypeOptions}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="충전/환전 구분"
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: { ...prev.parameters, charge_type: option.value },
                                        triggerSearch: false,
                                    }));
                                }}
                            />
                        </div>

                        <div className="item-select">
                            <Select
                                options={mileageUsedTypeOptions}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="적립/사용 구분"
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: { ...prev.parameters, used_type: option.value },
                                        triggerSearch: false,
                                    }));
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-input" style={{ width: "150px" }}>
                            <input
                                type="text"
                                placeholder="로그인아이디.."
                                name="login_id"
                                value={parameters.login_id}
                                onChange={handleParameterChange}
                            />
                        </div>

                        <div className="item-input" style={{ width: "150px" }}>
                            <input
                                type="text"
                                placeholder="업체명.."
                                name="rent_corp_name"
                                value={parameters.rent_corp_name}
                                onChange={handleParameterChange}
                            />
                        </div>

                        <div className="item-input" style={{ width: "150px" }}>
                            <input
                                type="text"
                                placeholder="입력하세요."
                                name="query"
                                value={parameters.query}
                                onChange={handleParameterChange}
                            />
                        </div>
                        <div className="item-buttons">
                            <button type="button" className="btn-search" onClick={handleSearch}>
                                검색
                            </button>
                            <button
                                type="button"
                                className="btn-search"
                                onClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        ...pageFilter,
                                        triggerSearch: true,
                                    }));
                                    setClientLabel({ value: null, label: "전체" });
                                }}
                            >
                                초기화
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
